import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { ArrowLeft } from "lucide-react";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { AppLink } from "@/components/app-link";
import { PageHeader } from "@/components/layout";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRouteParam } from "@/lib/use-route-param";

export const Route = createFileRoute("/revCollab/documents/$documentId")({
  component: RevCollabDocumentPage,
});

export function RevCollabDocumentPage() {
  const documentId = useRouteParam("documentId");
  const { data: details } = useQuery(
    convexQuery(
      api.revCollab.getHubDocument,
      documentId ? { documentId: documentId as Id<"projectDocuments"> } : "skip"
    )
  );
  return (
    <div className="space-y-6">
      <PageHeader
        title={details?.document?.name ?? "CollabPilot document"}
        description={details?.project?.name ?? ""}
        breadcrumbs={[
          { label: "CollabPilot", href: "/revCollab" },
          { label: details?.document?.name ?? "Document" },
        ]}
        actions={
          <AppLink
            to="/revCollab"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            Back
          </AppLink>
        }
      />

      {details === undefined ? (
        <Card>
          <CardContent className="py-10 text-sm text-muted-foreground">Loading revisions...</CardContent>
        </Card>
      ) : details === null || details.revisions.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-sm text-muted-foreground">
            No PDF/image revisions available for collaboration.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {details.revisions.map((revision) => (
            <Card key={revision._id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{revision.revisionName}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center justify-between gap-3">
                <div className="space-y-1 text-xs text-muted-foreground">
                  <p>
                    r{revision.revisionNumber} · {revision.originalFilename}
                  </p>
                  <p>
                    {revision.hasSession ? (
                      <Badge variant="secondary">Active history</Badge>
                    ) : (
                      <Badge variant="outline">No session yet</Badge>
                    )}
                  </p>
                </div>
                <AppLink
                  to="/revCollab/revisions/$revisionId"
                  params={{ revisionId: revision._id }}
                  title={`CollabPilot · ${details.document.name} r${revision.revisionNumber}`}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Open session
                </AppLink>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
